import React, { useState } from "react";
import { Modal, Upload, Button, message, Alert, Typography } from "antd";
import { UploadOutlined, FileTextOutlined, FileExcelOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { fileServices } from "~/services/file/file.services";
import { DocumentType, DocumentStatus } from "~/utils/constants";
import { useAuth } from "~/store";
import { useCreateFile } from "~/services/file/file.mutations";
import { baseService } from "~/services/base";

const { Text } = Typography;

interface ImportInventoryModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ImportInventoryModal: React.FC<ImportInventoryModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { user } = useAuth();
  const createFileMutation = useCreateFile();

  const handleUploadChange = (info: any) => {
    let newFileList = [...info.fileList];
    // Limit to only one file
    newFileList = newFileList.slice(-1);
    
    // Validate file type
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      if (file) {
        const isValidType = 
          file.type === "text/csv" || 
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".csv") ||
          file.name.toLowerCase().endsWith(".pdf");
        
        if (!isValidType) {
          message.error("Please upload a CSV or PDF file");
          newFileList = [];
        } else if (file.size > 10 * 1024 * 1024) {
          message.error("File size must be less than 10MB");
          newFileList = [];
        }
      }
    }
    
    setFileList(newFileList);
  };

  const handleSubmit = async () => {
    if (fileList.length === 0) {
      message.error("Please select a CSV or PDF file to upload");
      return;
    }

    if (!user?.client?.id) {
      message.error("Client information not found");
      return;
    }

    setIsUploading(true);

    try {
      const file = fileList[0].originFileObj;
      if (!file) throw new Error("File not found");

      // Upload file directly using the file service
      const fileData = {
        file: file,
        title: `Inventory Import - ${file.name}`,
        type: DocumentType.INVENTORY,
        status: DocumentStatus.SENT,
        clientId: user.client.id,
        visibleToStaff: true,
        visibleToClient: false,
      };

      await createFileMutation.mutateAsync(fileData);
      
      message.success("Inventory import file uploaded successfully. An admin will review and import it shortly.");
      setFileList([]);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Error uploading file:", error);
      message.error(error.response?.data?.message || "Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Import Inventory"
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isUploading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isUploading}
          disabled={fileList.length === 0}
          className="border-2 border-black bg-black text-white font-bold text-xs uppercase tracking-wide hover:opacity-90"
        >
          Upload for Review
        </Button>,
      ]}
      width={600}
      className="[&_.ant-modal-content]:bg-[#fffff5] [&_.ant-modal-content]:border-2 [&_.ant-modal-content]:border-black"
    >
      <div className="space-y-4">
        <Alert
          message="Upload Inventory File"
          description={
            <div className="mt-2">
              <Text className="text-sm">
                Upload a CSV or PDF file containing your inventory list. An admin will review and import it into the system.
              </Text>
              <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                <li>Accepted formats: CSV or PDF</li>
                <li>Maximum file size: 10MB</li>
                <li>You will be notified once the inventory has been imported</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          className="border-2 border-black"
        />

        <div>
          <Text strong className="text-sm uppercase tracking-wide mb-2 block">
            Select File
          </Text>
          <Upload
            accept=".csv,.pdf"
            fileList={fileList}
            onChange={handleUploadChange}
            beforeUpload={() => false} // Prevent auto-upload
            maxCount={1}
            disabled={isUploading}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={isUploading}
              className="border-2 border-black bg-white text-black font-bold text-xs uppercase tracking-wide hover:bg-gray-100"
            >
              Choose File
            </Button>
          </Upload>
          {fileList.length > 0 && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              {fileList[0].name?.toLowerCase().endsWith(".pdf") ? (
                <FileTextOutlined className="text-red-500" />
              ) : (
                <FileExcelOutlined className="text-green-500" />
              )}
              <Text>{fileList[0].name}</Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

